import { z } from "zod";

/** Types d'inventaire pour lesquels l'IA propose des suggestions à partir d'une photo. */
export const AiSuggestionInventoryTypeSchema = z.enum(["ARBRE", "ALIGNEMENT"]);
export type AiSuggestionInventoryType = z.infer<typeof AiSuggestionInventoryTypeSchema>;

export const AiSpeciesCandidateSchema = z.object({
  genre: z.string(),
  name: z.string(),
  nom_fr: z.string().nullable(),
  /** Identifiant dans la bibliothèque d'espèces ; null si l'espèce proposée n'y figure pas. */
  espece_id: z.number().int().nullable(),
  confidence: z.number().min(0).max(1),
});
export type AiSpeciesCandidate = z.infer<typeof AiSpeciesCandidateSchema>;

/** Une valeur proposée pour un champ de la fiche (identifiant du `fields.json` + libellé affiché). */
export const AiFieldValueSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type AiFieldValue = z.infer<typeof AiFieldValueSchema>;

/** Un champ de la fiche d'inventaire renseigné par l'IA, avec sa section d'origine (comme dans le formulaire). */
export const AiSuggestedFieldSchema = z.object({
  /** Nom du champ dans `fields.json` (ex. « etat-sanitaire-tronc »). */
  name: z.string(),
  label: z.string(),
  fieldset: z.string(),
  category: z.number(),
  kind: z.enum(["single", "multiple"]),
  values: z.array(AiFieldValueSchema).min(1),
  confidence: z.number().min(0).max(1),
});
export type AiSuggestedField = z.infer<typeof AiSuggestedFieldSchema>;

/** Suggestions renvoyées au navigateur. Rien n'est enregistré : l'utilisateur valide avant tout usage. */
export const AiPhotoSuggestionSchema = z.object({
  /** Identifiant public du journal d'usage, utilisé pour envoyer le retour utilisateur. */
  suggestion_id: z.string(),
  inventory_type: AiSuggestionInventoryTypeSchema,
  species: z.array(AiSpeciesCandidateSchema).max(3),
  fields: z.array(AiSuggestedFieldSchema),
  /** Même contenu que `fields`, au format du formulaire d'inventaire existant (nom du champ → identifiant(s)). */
  form_data: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
  observations: z.string().nullable(),
  model: z.string(),
  latency_ms: z.number(),
});
export type AiPhotoSuggestion = z.infer<typeof AiPhotoSuggestionSchema>;

export const AiSuggestionFeedbackSchema = z.object({
  /** « applied » : l'utilisateur a créé un inventaire à partir de la suggestion. */
  feedback: z.enum(["useful", "not_useful", "applied"]),
});
export type AiSuggestionFeedback = z.infer<typeof AiSuggestionFeedbackSchema>;

/** État de la fonctionnalité pour les utilisateurs autorisés (les réglages complets restent réservés aux admins). */
export const AiStatusSchema = z.object({
  enabled: z.boolean(),
  vision_available: z.boolean(),
  /** Suggestions maximum par mois pour le groupe de l'utilisateur ; 0 = illimité. */
  monthly_limit: z.number(),
  used_this_month: z.number(),
});
export type AiStatus = z.infer<typeof AiStatusSchema>;
